import { useState } from 'react';
import { Link } from 'react-router-dom';

import { getTriggerEventInfo } from './utils';
import { Button, DropdownMenu, Typography } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import queryString from 'query-string';

import { Location } from '@cobliteam/common-types';

import type { TriggerEventNotification } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';

import { Analytics } from '@cobliteam/analytics';
import { formatYesterdayOrToday, getTime } from '@cobliteam/utils/date';

import { useMarkNotificationAsRead } from '@cobliteam/hooks/src/useEntity/useAlerts';

import Icon from '@cobliteam/ui/icons';

interface TriggerNotificationItemProps {
  triggerNotification: TriggerEventNotification;
  isRead: boolean;
}

const TriggerNotificationItem = ({ triggerNotification, isRead }: TriggerNotificationItemProps) => {
  const { createdAt, readAt, triggerEvent, id } = triggerNotification;
  const { trigger, eventTime, meta, vehicleId, id: eventId } = triggerEvent;
  const { conditionCategory, vehicle, driver, address } = meta;
  const { name } = trigger;
  const { icon, rule, condition, eventType, path } = getTriggerEventInfo(triggerEvent);

  const [markAsRead, setMarkAsRead] = useState(Boolean(readAt) || isRead);
  const { mutate } = useMarkNotificationAsRead(id, createdAt);

  const handleMarkAsRead = () => {
    if (markAsRead) return;

    mutate();
    setMarkAsRead(true);
  };

  if (!conditionCategory) return null;

  return (
    <div className={cn('m-2.5 space-y-2.5 rounded border border-gray-light p-2.5', markAsRead ? 'hover:bg-gray-lightest' : 'bg-primary-2')} onMouseEnter={handleMarkAsRead}>
      <div className='flex items-center justify-between gap-2.5'>
        <div className='flex items-baseline gap-1'>
          {markAsRead ? undefined : <Icon name='circle' color='primary' className='size-2 fill-primary' />}
          <Typography variant='caption-medium' color='secondary'>
            {rule}
          </Typography>
        </div>
        <Typography variant='chip-label' color='secondary'>
          {formatYesterdayOrToday(Date.now(), new Date(eventTime).valueOf(), '-')}
        </Typography>
      </div>

      <div className='flex items-center gap-1'>
        {icon}
        <Typography variant='description-title' color={markAsRead ? 'secondary' : 'primary'}>
          {name}
        </Typography>
      </div>

      <div className='ml-7'>
        <Typography variant='caption-default' color='secondary'>
          <b>Condição: </b> {condition}
        </Typography>

        <Typography variant='caption-default' color='secondary'>
          <b>Veículo: </b> {vehicle?.licensePlate} - {vehicle?.model}
        </Typography>

        <Typography variant='caption-default' color='secondary'>
          <b>Motorista: </b> {driver?.name || 'Sem motorista associado'}
        </Typography>

        <Typography variant='caption-default' color='secondary'>
          <b>Endereço: </b> {address}
        </Typography>
      </div>

      <div className='flex gap-2'>
        <Link
          to={{
            pathname: Location.TRACKING,
            search: `?${queryString.stringify({
              vehicle_id: vehicleId,
              date: getTime(new Date(eventTime), 'start'),
              event_type: eventType,
              event_id: eventId,
              event_time: getTime(new Date(eventTime), 'start'),
            })}`,
          }}
          target='_blank'
          rel='noopener noreferrer'
          className='ml-auto flex w-fit'
          onClick={() => Analytics.track('Notification Tracking History Clicked')}
        >
          <Button variant='outlined' color='secondary' size='condensed' startIcon={<Icon name='route' />}>
            Ver trajeto
          </Button>
        </Link>

        <DropdownMenu>
          <DropdownMenu.Trigger>
            <Button variant='outlined' size='condensed' icon={<Icon name='ellipsis' />} className='size-7 p-0' onClick={() => Analytics.track('More Options Button Clicked')} />
          </DropdownMenu.Trigger>

          <DropdownMenu.Content align='end' portal>
            <DropdownMenu.Item>
              <Link
                className='flex items-center gap-2.5 font-bold'
                to={{
                  pathname: Location.TRIGGERS_EDIT,
                  search: `?triggerId=${trigger.id}`,
                }}
                onClick={() => Analytics.track('Notification Rule Edit Clicked')}
              >
                <Icon name='pencil' />
                Editar regra
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item>
              <Link
                to={path}
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-2.5 font-bold'
                onClick={() => Analytics.track('Notification Fleet Policy History Clicked')}
              >
                <Icon name='list-checks' />
                Ver histórico da regra
              </Link>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TriggerNotificationItem;
